/**
 * @fileoverview 미납신고 관련 React Query 커스텀 훅
 *
 * 이 모듈은 헬프데스크의 미납신고 기능을 위한 React Query 훅들을 제공합니다.
 * 미납신고 목록 조회, 상세 조회, 취소 기능을 포함하며,
 * 서버에서 가져온 데이터의 스키마 검증을 포함합니다.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getNonpaidReport, getNonpaidReports } from "@features/helpdesk/api";
import { badUserComplaintIDType, GetNonpaidReportsType, GetNonpaidReportType, ReqNonpaidReportsType } from "@features/helpdesk/model/getNonpaidReportsModel";
import { GetNonpaidReportCancelType, ReqNonpaidReportCancelType } from "@features/helpdesk/model/getNonpaidReportCancelModel";
import { getNonpaidReportCancel } from "@features/helpdesk/api/getNonpaidReportCancel";

/**
 * React Query 쿼리 키 상수 정의
 * 캐시 키 관리와 타입 안전성을 위한 상수들
 */
export const QUERY_KEYS = {
  /** 미납신고 목록 조회 쿼리 키 */
  GET_NONPAID_REPORTS: "getNonpaidReports",
  /** 미납신고 상세 조회 쿼리 키 */
  GET_NONPAID_REPORT: "getNonpaidReport",
} as const;

/**
 * 미납신고 목록을 조회하는 React Query 훅
 *
 * @description
 * - API에서 미납신고 목록을 가져옵니다
 * - 페이지네이션 파라미터를 포함한 필터링을 지원합니다
 * - 서버 응답 데이터에 대한 스키마 검증을 수행합니다
 * - 검증 실패 시 null을 반환합니다
 *
 * @param {ReqNonpaidReportsType} params - 미납신고 목록 조회 파라미터
 * @returns
 * - data: 검증된 미납신고 목록 데이터 또는 null
 * - isLoading: 로딩 상태
 * - isSuccess: 성공 상태
 * - error: 에러 정보
 */
export const useNonpaidReports = (params: ReqNonpaidReportsType) => {
  return useQuery<GetNonpaidReportsType | null, Error>({
    queryKey: [QUERY_KEYS.GET_NONPAID_REPORTS, params],
    queryFn: () => getNonpaidReports(params),
  });
};

/**
 * 특정 미납신고의 상세 정보를 조회하는 React Query 훅
 *
 * @description
 * - API에서 특정 미납신고의 상세 정보를 가져옵니다
 * - 미납신고 ID를 기반으로 데이터를 조회합니다
 * - 서버 응답 데이터에 대한 스키마 검증을 수행합니다
 * - 검증 실패 시 null을 반환합니다
 *
 * @param {badUserComplaintIDType} badUserComplaintID - 조회할 미납신고의 ID
 * @returns
 * - data: 검증된 미납신고 상세 데이터 또는 null
 * - isLoading: 로딩 상태
 * - isSuccess: 성공 상태
 * - error: 에러 정보
 */
export const useNonpaidReport = (badUserComplaintID: badUserComplaintIDType) => {
  return useQuery<GetNonpaidReportType | null, Error>({
    queryKey: [QUERY_KEYS.GET_NONPAID_REPORT, badUserComplaintID],
    queryFn: () => getNonpaidReport({ badUserComplaintID }),
  });
};

/**
 * 미납신고 취소를 위한 React Query 뮤테이션 훅
 *
 * @description
 * - 미납신고 취소 인증을 요청합니다
 * - 성공 시 관련된 미납신고 쿼리들을 자동으로 무효화합니다
 * - 서버 응답 데이터에 대한 스키마 검증을 수행합니다
 * - 검증 실패 시 null을 반환합니다
 *
 * @returns
 * - mutate: 미납신고 취소 요청 함수
 * - isLoading: 뮤테이션 진행 상태
 * - isSuccess: 성공 상태
 * - error: 에러 정보
 */
export const useNonpaidReportCancel = () => {
  const queryClient = useQueryClient();

  return useMutation<GetNonpaidReportCancelType | null, Error, ReqNonpaidReportCancelType>({
    mutationFn: (params) => getNonpaidReportCancel(params),
    onSuccess: () => {
      // 미납신고 관련 모든 쿼리 무효화
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_NONPAID_REPORTS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_NONPAID_REPORT],
      });
    },
  });
};
